// Portfolio fonts: the families a creator can pick for Headline and Body in
// the Design panel, and on-demand loading for the ones the app doesn't load
// up front.
//
// apps/app/index.html loads only Inter and Playfair Display. Every other family
// here is fetched from Google Fonts the first time something renders with it,
// by appending one <link rel="stylesheet"> per family to <head>. Without this,
// picking e.g. Fraunces silently rendered Georgia. The font picker previews
// every option at its regular weight, one small stylesheet per family so a
// single rejected family can't blank the rest.

export interface PortfolioFont {
  label: string;
  /** CSS font-family value stored in the theme. */
  value: string;
  /**
   * Google Fonts css2 `family=` spec, or null when index.html already loads
   * the family.
   */
  googleSpec: string | null;
  /**
   * True for display families Google Fonts ships at weight 400 only. The
   * portfolio uses semibold/bold in places; rendering these with
   * `font-synthesis-weight: none` avoids a smeared faux bold.
   */
  singleWeight?: boolean;
}

type FontCategory = "serif" | "sans" | "mono";

const FALLBACK_STACK: Record<FontCategory, string> = {
  serif: "Georgia, serif",
  sans: "system-ui, sans-serif",
  mono: "ui-monospace, monospace",
};

const googleFont = (
  label: string,
  category: FontCategory,
  googleSpec: string,
): PortfolioFont => ({
  label,
  value: `'${label}', ${FALLBACK_STACK[category]}`,
  googleSpec,
});

/** A family Google Fonts ships at weight 400 only (no `wght` axis to request). */
const singleWeightFont = (
  label: string,
  category: FontCategory,
): PortfolioFont => ({
  ...googleFont(label, category, label.replace(/ /g, "+")),
  singleWeight: true,
});

// Weights the portfolio renders (font-normal..font-bold), trimmed to what
// each family ships; Google Fonts rejects the whole request on a missing one.
const W4_7 = "wght@400;500;600;700";

/**
 * The original six portfolio families. Their `value` strings are persisted in
 * saved themes, so they must never change.
 */
const ORIGINAL_FONTS: readonly PortfolioFont[] = [
  { label: "Inter", value: "'Inter', system-ui, sans-serif", googleSpec: null },
  {
    label: "Playfair Display",
    value: "'Playfair Display', Georgia, serif",
    googleSpec: null,
  },
  {
    label: "Instrument Serif",
    value: "'Instrument Serif', Georgia, serif",
    googleSpec: "Instrument+Serif:ital@0;1",
  },
  {
    label: "Fraunces",
    value: "'Fraunces', Georgia, serif",
    googleSpec: "Fraunces:ital,opsz,wght@0,9..144,300..900;1,9..144,300..900",
  },
  {
    label: "Space Grotesk",
    value: "'Space Grotesk', system-ui, sans-serif",
    googleSpec: "Space+Grotesk:wght@300..700",
  },
  {
    label: "DM Sans",
    value: "'DM Sans', system-ui, sans-serif",
    googleSpec: "DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000",
  },
];

/**
 * The brand application page font catalog (aspirex
 * `studio/Campaigns/CampaignSettingsPage/tabs/applicationPage/constants.ts`,
 * HEADLINE_ + BODY_FONT_FAMILY_OPTIONS, in source order), minus the families
 * already in ORIGINAL_FONTS (Inter, Playfair Display, DM Sans) and the
 * "System Default" entry.
 */
const BRAND_APPLICATION_FONTS: readonly PortfolioFont[] = [
  // Headline list
  googleFont("Oswald", "sans", `Oswald:${W4_7}`),
  googleFont("Merriweather", "serif", `Merriweather:${W4_7}`),
  googleFont("Josefin Sans", "sans", `Josefin+Sans:${W4_7}`),
  singleWeightFont("Bebas Neue", "sans"),
  googleFont("Cairo", "sans", `Cairo:${W4_7}`),
  googleFont("EB Garamond", "serif", `EB+Garamond:${W4_7}`),
  googleFont("Manrope", "sans", `Manrope:${W4_7}`),
  googleFont("Exo 2", "sans", `Exo+2:${W4_7}`),
  singleWeightFont("Abril Fatface", "serif"),
  googleFont("Cormorant Garamond", "serif", `Cormorant+Garamond:${W4_7}`),
  singleWeightFont("Righteous", "sans"),
  googleFont("Cinzel", "serif", `Cinzel:${W4_7}`),
  singleWeightFont("Didact Gothic", "sans"),
  googleFont("Vollkorn", "serif", `Vollkorn:${W4_7}`),
  singleWeightFont("Prata", "serif"),
  googleFont("Montserrat", "sans", `Montserrat:${W4_7}`),
  googleFont("IBM Plex Mono", "mono", `IBM+Plex+Mono:${W4_7}`),
  googleFont("JetBrains Mono", "mono", `JetBrains+Mono:${W4_7}`),
  // Body-only additions
  googleFont("Open Sans", "sans", `Open+Sans:${W4_7}`),
  googleFont("Noto Sans", "sans", `Noto+Sans:${W4_7}`),
  googleFont("Ubuntu", "sans", "Ubuntu:wght@400;500;700"),
  googleFont("Lora", "serif", `Lora:${W4_7}`),
  googleFont("Fira Sans", "sans", `Fira+Sans:${W4_7}`),
  googleFont("Arimo", "sans", `Arimo:${W4_7}`),
  googleFont("IBM Plex Sans", "sans", `IBM+Plex+Sans:${W4_7}`),
  googleFont("Crimson Text", "serif", "Crimson+Text:wght@400;600;700"),
];

/** Every family offered by both the Headline and Body pickers. */
export const PORTFOLIO_FONTS: readonly PortfolioFont[] = [
  ...ORIGINAL_FONTS,
  ...BRAND_APPLICATION_FONTS,
];

const GOOGLE_FONTS_CSS2 = "https://fonts.googleapis.com/css2";

/** The first family name in a CSS font-family value, unquoted. */
export function primaryFamily(fontFamily: string): string {
  return (fontFamily.split(",")[0] ?? "").trim().replace(/^['"]|['"]$/g, "");
}

function findFont(fontFamily: string): PortfolioFont | undefined {
  const name = primaryFamily(fontFamily);
  return PORTFOLIO_FONTS.find((f) => primaryFamily(f.value) === name);
}

/** The css2 URL for one `family=` spec. */
function css2Url(spec: string): string {
  return `${GOOGLE_FONTS_CSS2}?family=${spec}&display=swap`;
}

/** Stable DOM id suffix for a family name. */
function familySlug(family: string): string {
  return family.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/** The Google Fonts stylesheet URL for a family, or null if none is needed. */
export function fontStylesheetUrl(fontFamily: string): string | null {
  const spec = findFont(fontFamily)?.googleSpec;
  return spec ? css2Url(spec) : null;
}

/**
 * `font-synthesis-weight` for a CSS font-family value: "none" for 400-only
 * families (no faux bold), "auto" otherwise.
 */
export function fontSynthesisWeight(fontFamily: string): "none" | "auto" {
  return findFont(fontFamily)?.singleWeight ? "none" : "auto";
}

/**
 * The picker's preview stylesheets: one URL per remote family at its regular
 * weight (the `family=` name without axes). Separate requests, because css2
 * rejects a whole request if any one family in it is invalid.
 */
export function fontPreviewStylesheetUrls(
  fonts: readonly PortfolioFont[] = PORTFOLIO_FONTS,
): { family: string; href: string }[] {
  return fonts.flatMap((f) =>
    f.googleSpec
      ? [{ family: f.label, href: css2Url(f.googleSpec.split(":")[0]) }]
      : [],
  );
}

function appendStylesheet(id: string, href: string): void {
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Load a font family if the app doesn't already. Idempotent (one link per
 * family), a no-op for unknown or preloaded families, and safe outside a
 * browser. Public pages call this only for the theme's selected families.
 */
export function ensureFontLoaded(fontFamily: string): void {
  if (typeof document === "undefined") return;
  const href = fontStylesheetUrl(fontFamily);
  if (!href) return;
  appendStylesheet(`pf-font-${familySlug(primaryFamily(fontFamily))}`, href);
}

/**
 * Load the font picker's previews, one stylesheet per family. Idempotent and
 * safe outside a browser; called when the picker opens, never on public pages.
 */
export function ensureFontPreviewsLoaded(): void {
  if (typeof document === "undefined") return;
  for (const { family, href } of fontPreviewStylesheetUrls()) {
    appendStylesheet(`pf-font-preview-${familySlug(family)}`, href);
  }
}
